import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    IconButton,
    Snackbar,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import ContentCopy from "@mui/icons-material/ContentCopy";
import dayjs, { Dayjs } from "dayjs";
import moment from "moment";

import Layout from "components/Layout";
import { MainTable } from "components/MainTable";
import MainModal from "components/MainModal";
import NoData from "components/NoData/NoData.view";
import { generateApiKey, isApiKeyActive, IThirdPartyApiKey, rotateApiKey } from "api/queries/apiKeysAPI";
import { ApiKeysEntity } from "entities/ApiKeys/ApiKeysEntity";
import { useAllowApiKeyManagement } from "hooks/useAllowApiKeyManagement";
import useRolesPermissions from "hooks/useRolesPermissions";
import useDateFormat from "hooks/useDateFormat";
import DowntimeAppDrawerItems from "../components/DowntimeAppDrawerItems";
import { useApiKeyColumns } from "./hooks/useApiKeyColumns";
import { useStyles } from "./APIKeyManagement.styles";

const KEYS = "corporate_backoffice.api_key_management";

// The mint endpoints reject a 6th active key with HTTP 409 (contract §5.2.1). axiosInstance has
// no response interceptor, so the status reaches us intact; duck-typed so it never throws on a
// non-axios rejection.
const isCapReachedError = (error: unknown): boolean =>
    (error as { response?: { status?: number } })?.response?.status === 409;

interface ISnackbarState {
    message: string;
    open: boolean;
    severity: "error" | "success";
}

export const APIKeyManagement = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { getDateFormatByLanguage } = useDateFormat();
    const { CORPORATE_PERMISSIONS } = useRolesPermissions();
    const { isAllowedToUseApiKeyManagement, isAllowLoading, maxActiveApiKeys } = useAllowApiKeyManagement();

    const canGenerate = Boolean(CORPORATE_PERMISSIONS?.sidebarPermissions?.canGenerateApiKey);
    const canRevoke = Boolean(CORPORATE_PERMISSIONS?.sidebarPermissions?.canRevokeApiKey);

    // "Active APIs" filter — default on. Passed to the entity so the filtering happens at the
    // query level (Hasura where), mirroring SecondaryCodeRequirements' showOnlyPending.
    const [activeOnly, setActiveOnly] = useState(true);

    const { apiKeys, apolloQueryError, loading, refetch } = ApiKeysEntity.GetThirdPartyApiKeys(
        !isAllowedToUseApiKeyManagement,
        activeOnly,
    );
    const { revokeKey } = ApiKeysEntity.RevokeThirdPartyApiKey();

    const [generateOpen, setGenerateOpen] = useState(false);
    const [labelInput, setLabelInput] = useState("");
    const [expiresInput, setExpiresInput] = useState<Dayjs | null>(null);
    const [revealedKey, setRevealedKey] = useState<string | null>(null);
    const [revokeTarget, setRevokeTarget] = useState<IThirdPartyApiKey | null>(null);
    const [rotateTarget, setRotateTarget] = useState<IThirdPartyApiKey | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState<ISnackbarState>({ message: "", open: false, severity: "success" });

    const columns = useApiKeyColumns({ canGenerate, canRevoke, onRevoke: setRevokeTarget, onRotate: setRotateTarget });

    // Cap gate counts active keys in the current result: with the filter on the query already
    // returns only active rows (count = length); with it off we count the active ones here.
    // Either way, revoking/expiring a key drops the count and re-enables Generate.
    const activeCount = useMemo(() => apiKeys.filter(isApiKeyActive).length, [apiKeys]);
    // Proactive Generate gate against the Corporation's DB-driven cap (threaded from the config
    // read, D-2552-3); the 409 catch below stays as the fallback for a stale count. `!loading`
    // guards the "unknown count" window: while the key list is (re)fetching it is momentarily
    // empty, which counts as 0 active and would falsely read as "under cap" — so `atCap` (and its
    // tooltip) stay dormant until a real count arrives.
    const atCap = !loading && activeCount >= maxActiveApiKeys;
    // Start disabled and stay disabled until the count is known: `loading` covers the initial
    // fetch and every post-mutation refetch, so Generate never flashes enabled → disabled on a
    // page refresh (the count resolving 0 → cap).
    const generateDisabled = loading || atCap;

    const notify = (message: string, severity: "error" | "success") => setSnackbar({ message, open: true, severity });
    const closeSnackbar = () => setSnackbar((current) => ({ ...current, open: false }));

    // Empty picker ⇒ undefined, so the backend applies its default expiry (now + 1 year).
    const selectedExpiryIso = (): string | undefined =>
        expiresInput ? expiresInput.endOf("day").toISOString() : undefined;

    // disablePast only constrains the calendar popup — a hand-typed date can still be invalid
    // (toISOString would throw) or in the past (backend 400). Block submission instead.
    const expiryInvalid = Boolean(
        expiresInput && (!expiresInput.isValid() || expiresInput.endOf("day").isBefore(dayjs())),
    );

    // A refetch failure must not override the outcome toast of the operation that just
    // succeeded — the list error surfaces via apolloQueryError instead.
    const refreshList = useCallback(() => refetch().catch(() => undefined), [refetch]);

    const handleGenerate = async () => {
        setSubmitting(true);
        try {
            const created = await generateApiKey({ expires_at: selectedExpiryIso(), label: labelInput.trim() });
            setGenerateOpen(false);
            setLabelInput("");
            setExpiresInput(null);
            setRevealedKey(created.api_key);
            notify(t(`${KEYS}.generate_success`), "success");
            await refreshList();
        } catch (error) {
            notify(
                isCapReachedError(error)
                    ? t(`${KEYS}.cap_reached`, { max: maxActiveApiKeys })
                    : t(`${KEYS}.generate_error`),
                "error",
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleRotate = async () => {
        if (!rotateTarget) {
            return;
        }
        setSubmitting(true);
        try {
            const created = await rotateApiKey(rotateTarget.third_party_api_key_id, {});
            setRotateTarget(null);
            setRevealedKey(created.api_key);
            notify(t(`${KEYS}.rotate_success`), "success");
            await refreshList();
        } catch (error) {
            // Rotating an expired key while at the cap 409s too (it would resurrect a net-new
            // active key) — surface the cap reason rather than the generic rotate error.
            notify(
                isCapReachedError(error)
                    ? t(`${KEYS}.cap_reached`, { max: maxActiveApiKeys })
                    : t(`${KEYS}.rotate_error`),
                "error",
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleRevoke = async () => {
        if (!revokeTarget) {
            return;
        }
        setSubmitting(true);
        try {
            const result = await revokeKey({
                variables: {
                    id: revokeTarget.third_party_api_key_id,
                    key_id_text: String(revokeTarget.third_party_api_key_id),
                    revoked_at: moment().toISOString(),
                },
            });
            setRevokeTarget(null);
            // affected_rows 0 = the row no longer matched (already revoked elsewhere / stale
            // view) — Hasura reports that as success, so check it before claiming the revoke.
            if (result.data?.update_third_party_api_key?.affected_rows) {
                notify(t(`${KEYS}.revoke_success`), "success");
            } else {
                notify(t(`${KEYS}.revoke_error`), "error");
            }
            await refreshList();
        } catch {
            notify(t(`${KEYS}.revoke_error`), "error");
        } finally {
            setSubmitting(false);
        }
    };

    const handleCopy = async () => {
        if (!revealedKey) {
            return;
        }
        // The raw key can never be re-shown — only claim success if the clipboard write actually
        // succeeded (the API is absent on non-secure origins and can be permission-denied).
        try {
            if (!navigator.clipboard) {
                throw new Error("Clipboard API unavailable");
            }
            await navigator.clipboard.writeText(revealedKey);
            notify(t(`${KEYS}.copied`), "success");
        } catch {
            notify(t(`${KEYS}.copy_error`), "error");
        }
    };

    const bodyComponent = useMemo(() => {
        if (loading) {
            return (
                <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                    <CircularProgress color="info" size={20} />
                </Box>
            );
        }
        // A failed list read is not an empty tenant — show the error, not "no keys yet".
        if (apolloQueryError) {
            return (
                <Box className={classes.noData}>
                    <Typography className={classes.fontFamily}>{t(`${KEYS}.load_error`)}</Typography>
                    <Button className={classes.fontFamily} variant="outlined" onClick={refreshList}>
                        {t(`${KEYS}.retry`)}
                    </Button>
                </Box>
            );
        }
        if (apiKeys.length === 0) {
            // The query is filtered server-side: an empty result under the active-only filter
            // means "no active keys" (revoked/expired ones may still exist); with the filter
            // off it means the tenant has no keys at all.
            return (
                <Box className={classes.noData}>
                    <NoData title={t(activeOnly ? `${KEYS}.no_active_keys` : `${KEYS}.no_keys`)} />
                </Box>
            );
        }
        return (
            <MainTable
                columns={columns}
                rows={apiKeys}
                pagination
                customStyleTypography={{ fontFamily: "Source Sans 3" }}
            />
        );
    }, [loading, apolloQueryError, apiKeys, activeOnly, columns, classes.noData, classes.fontFamily, refreshList, t]);

    return (
        <Layout appDrawerItems={<DowntimeAppDrawerItems />}>
            <Box className={classes.mainWrapper}>
                {/* While the gate read is in flight the answer is unknown — spinner, not the
                    "not enabled" message (it would flash for every allowed admin). */}
                {isAllowLoading ? (
                    <Box className={classes.detailsBox}>
                        <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                            <CircularProgress color="info" size={20} />
                        </Box>
                    </Box>
                ) : !isAllowedToUseApiKeyManagement ? (
                    <Box className={classes.detailsBox}>
                        <Typography className={`${classes.notAvailable} ${classes.fontFamily}`}>
                            {t(`${KEYS}.not_available`)}
                        </Typography>
                    </Box>
                ) : (
                    <Box className={classes.detailsBox}>
                        <div className={classes.card}>
                            <div className={classes.cardHeader}>
                                <span className={classes.mainWrapperHeaderTitle}>{t(`${KEYS}.title`)}</span>
                                <div className={classes.headerActions}>
                                    <FormControlLabel
                                        className={classes.filterCheckboxLabel}
                                        control={
                                            <Checkbox
                                                className={classes.filterCheckbox}
                                                checked={activeOnly}
                                                onChange={(event) => setActiveOnly(event.target.checked)}
                                            />
                                        }
                                        label={t(`${KEYS}.active_filter_label`)}
                                    />
                                    {canGenerate && (
                                        // Span wrapper so the tooltip fires over the disabled button (MUI idiom).
                                        <Tooltip
                                            placement="top"
                                            PopperProps={{ className: classes.tooltip }}
                                            title={
                                                atCap ? (
                                                    <Typography className={classes.tooltipTexts}>
                                                        {t(`${KEYS}.cap_reached`, { max: maxActiveApiKeys })}
                                                    </Typography>
                                                ) : (
                                                    ""
                                                )
                                            }
                                        >
                                            <span>
                                                <Button
                                                    className={classes.fontFamily}
                                                    variant="contained"
                                                    disabled={generateDisabled}
                                                    onClick={() => setGenerateOpen(true)}
                                                >
                                                    {t(`${KEYS}.generate_button`)}
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                            <div className={classes.cardBody}>{bodyComponent}</div>
                        </div>
                    </Box>
                )}
            </Box>

            <MainModal
                isOpen={generateOpen}
                onClose={() => setGenerateOpen(false)}
                title={t(`${KEYS}.generate_modal_title`)}
                contentClassName={classes.modalBody}
                okButton={t(`${KEYS}.generate_confirm`)}
                cancelButton={t(`${KEYS}.cancel`)}
                onOkButtonClick={handleGenerate}
                onCancelButtonClick={() => setGenerateOpen(false)}
                disableOkButton={!labelInput.trim() || expiryInvalid || submitting}
                showCloseButton
            >
                <div className={classes.form}>
                    <TextField
                        className={classes.textField}
                        variant="outlined"
                        label={t(`${KEYS}.label_field`)}
                        value={labelInput}
                        onChange={(event) => setLabelInput(event.target.value)}
                        inputProps={{ maxLength: 255 }}
                        fullWidth
                    />
                    <FormControl className={classes.textField} fullWidth>
                        <DatePicker
                            label={t(`${KEYS}.expires_field`)}
                            value={expiresInput}
                            onChange={(date) => setExpiresInput(date)}
                            format={getDateFormatByLanguage()}
                            disablePast
                        />
                    </FormControl>
                </div>
            </MainModal>

            <MainModal
                isOpen={Boolean(revealedKey)}
                onClose={() => setRevealedKey(null)}
                title={t(`${KEYS}.reveal_title`)}
                contentClassName={classes.modalBody}
                okButton={t(`${KEYS}.reveal_done`)}
                onOkButtonClick={() => setRevealedKey(null)}
                showCloseButton
            >
                <div className={classes.reveal}>
                    <Alert className={classes.fontFamily} severity="warning">
                        {t(`${KEYS}.reveal_warning`)}
                    </Alert>
                    <div className={classes.keyRow}>
                        <TextField
                            className={classes.fontFamily}
                            value={revealedKey || ""}
                            InputProps={{ readOnly: true }}
                            fullWidth
                        />
                        <Tooltip title={t(`${KEYS}.copy`)}>
                            <IconButton onClick={handleCopy} aria-label={t(`${KEYS}.copy`)}>
                                <ContentCopy />
                            </IconButton>
                        </Tooltip>
                    </div>
                </div>
            </MainModal>

            <MainModal
                isOpen={Boolean(revokeTarget)}
                onClose={() => setRevokeTarget(null)}
                title={t(`${KEYS}.revoke_modal_title`)}
                contentClassName={classes.modalBody}
                okButton={t(`${KEYS}.revoke`)}
                cancelButton={t(`${KEYS}.cancel`)}
                okButtonColor="error"
                onOkButtonClick={handleRevoke}
                onCancelButtonClick={() => setRevokeTarget(null)}
                disableOkButton={submitting}
            >
                <Typography className={classes.fontFamily}>{t(`${KEYS}.revoke_modal_description`)}</Typography>
            </MainModal>

            <MainModal
                isOpen={Boolean(rotateTarget)}
                onClose={() => setRotateTarget(null)}
                title={t(`${KEYS}.rotate_modal_title`)}
                contentClassName={classes.modalBody}
                okButton={t(`${KEYS}.rotate`)}
                cancelButton={t(`${KEYS}.cancel`)}
                okButtonColor="warning"
                onOkButtonClick={handleRotate}
                onCancelButtonClick={() => setRotateTarget(null)}
                disableOkButton={submitting}
            >
                <Typography className={classes.fontFamily}>{t(`${KEYS}.rotate_modal_description`)}</Typography>
            </MainModal>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={closeSnackbar}
                anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
            >
                <Alert className={classes.fontFamily} severity={snackbar.severity} onClose={closeSnackbar}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Layout>
    );
};
